import { useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ProductCard from '@/components/feature/ProductCard';
import { products, categories, categoryNameMap } from '@/mocks/data';
import { getWebPageSchema, getBreadcrumbSchema } from '@/lib/seo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'meirong-hufu';
  const categoryProducts = products.filter((p) => p.category === currentSlug);
  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const categoryName = categoryNameMap[currentSlug] || currentSlug;

  return (
    <div className="pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getWebPageSchema(
              `${categoryName} | 国产精品精选推荐`,
              currentCategory?.description || '精选国产优质好物',
              `https://anidndd.cn/category/${currentSlug}`
            )
          ),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbSchema([
              { name: '首页', url: 'https://anidndd.cn/' },
              { name: categoryName, url: `https://anidndd.cn/category/${currentSlug}` },
            ])
          ),
        }}
      />

      {/* Category Banner */}
      <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={currentCategory?.image || 'https://readdy.ai/api/search-image?query=Elegant%20minimal%20cream%20background%20with%20soft%20warm%20lighting%2C%20clean%20and%20spacious%20composition%20for%20product%20category%20showcase&width=1400&height=600&seq=cat-default&orientation=landscape'}
            alt={categoryName}
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">
            {categoryName}
          </h1>
          <p className="text-sm md:text-base text-background-200 max-w-xl text-center">
            {currentCategory?.description || '精选国产优质好物，品质值得信赖'}
          </p>
        </div>
      </section>

      {/* Breadcrumb + Category Tabs */}
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="py-4 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: categoryName },
            ]}
          />
        </div>

        {/* Category Selector */}
        <div className="py-5 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            {categories.map((cat) => (
              <a
                key={cat.slug}
                href={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800'
                }`}
              >
                {cat.name}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {categoryProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop>
              {categoryProducts.map((product) => (
                <ProductCard key={product.id} {...product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">该分类暂无产品，敬请期待</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}